import { useState, type ReactNode } from "react";

import {
  Button,
  Input as ReactAriaInput,
  type InputProps as ReactAriaInputProps,
  TextField as ReactAriaTextField,
  type TextFieldProps as ReactAriaTextFieldProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { EyeIcon, EyeSlashedIcon } from "#icons";
import { cn, customTv } from "#utils";

const inputWrapperStyles = customTv({
  base: `
    text-label-medium text-text-primary outline-none border border-outline-input-enabled bg-primary rounded-1 leading-md-20 truncate
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    relative flex items-center gap-xxs py-xs
  `,
  variants: {
    variant: {
      primary: "",
    },
    isReadOnly: {
      true: "!cursor-text !bg-background-secondary !text-text-primary !border-transparent hover:ring-0",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isInputFocused: {
      true: "ring-1 !ring-outline-input-focus !border-outline-input-focus",
    },
    isDisabled: {
      true: "cursor-not-allowed bg-background-primary text-text-disabled border-outline-input-disabled hover:ring-0 hover:border-outline-input-disabled",
    },
  },
});

interface InputProps extends ReactAriaInputProps {
  variant?: "primary";
  className?: string;
}

export interface SecretTextFieldProps extends ReactAriaTextFieldProps {
  inputProps?: InputProps;
  label: string;
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
}

export function SecretTextField({
  inputProps,
  label,
  tooltipText,
  className,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  isRequired,
  ...rest
}: SecretTextFieldProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleToggleVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ReactAriaTextField
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      type={showPassword ? "text" : "password"}
      {...rest}
    >
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <div
        className={inputWrapperStyles({
          variant: inputProps?.variant,
          isInvalid,
          isInputFocused,
          isDisabled,
          isReadOnly,
          className: inputProps?.className,
        })}
      >
        <ReactAriaInput
          {...inputProps}
          className={cn(
            "size-full border-0 bg-transparent pr-[2.5rem] outline-0",
            "flex-1 truncate disabled:cursor-not-allowed disabled:text-text-disabled",
            "placeholder:text-text-secondary focus-visible:outline-none disabled:placeholder:text-text-disabled",
            "px-lg",
          )}
          onBlur={(e) => {
            if (isReadOnly) return;

            setIsInputFocused(false);
            inputProps?.onBlur?.(e);
          }}
          onFocus={(e) => {
            if (isReadOnly) return;

            setIsInputFocused(true);
            inputProps?.onFocus?.(e);
          }}
          placeholder={inputProps?.placeholder || "Enter secret..."}
          type={showPassword ? "text" : "password"}
        />
        <Button
          aria-label="toggle password visibility"
          className={cn(
            "absolute right-0 px-md text-text-secondary hover:text-text-primary focus:text-text-primary",
            "outline-none transition-colors",
            isDisabled ? "cursor-not-allowed opacity-50" : "",
          )}
          isDisabled={isDisabled}
          onPress={handleToggleVisibility}
        >
          {showPassword ? <EyeIcon className="size-lg" /> : <EyeSlashedIcon className="size-lg" />}
        </Button>
      </div>
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaTextField>
  );
}
